import { ChangeEvent, FC, RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Tooltip, Typography } from '@mui/material';

import { ReactComponent as UploadIcon } from '@/assets/icons/import-icon.svg';
import AppButton from '@/shared/ui/AppButton';

import FileCard, { FileData } from './FileCard';
import {
  CloseButtonContainer,
  CloseIconButton,
  ErrorBanner,
  FileListContainer,
  RightSideContainer,
  SidebarHeader,
  TabButton,
  TabsContainer,
  UploadContainer,
} from './ImageSlider.styles';
import OperationSectionList, { OperationSection } from './OperationSectionList';

type Tab = 'Decoration' | 'Neutralization' | 'Artwork' | 'ArtworkPreview';

const noop = () => undefined;

interface ImageSidebarProps {
  tabs: readonly Tab[];
  activeTab: Tab;
  files: FileData[];
  monitorMode?: boolean;
  beforeFiles?: FileData[];
  afterFiles?: FileData[];
  selectedFileId: number | null;
  expandedFileIds: Set<number>;
  errorMessage: string | null;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onTabChange: (tab: Tab) => void;
  onClose: () => void;
  onUploadClick: () => void;
  onUploadSlotClick?: (slot: 'before' | 'after') => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onCardClick: (id: number) => void;
  onDeleteFile: (id: number) => void;
  maxFileSizeMb?: number;
  hasUploadOption?: boolean;
  hasDeleteOption?: boolean;
  hideTabs?: boolean;
  operationSections?: OperationSection[];
  initialExpandedOperationId?: string;
  onUploadToOperation?: (operationId?: string) => void;
}

const ImageSidebar: FC<ImageSidebarProps> = ({
  tabs,
  activeTab,
  files,
  monitorMode = false,
  beforeFiles = [],
  afterFiles = [],
  selectedFileId,
  expandedFileIds,
  errorMessage,
  fileInputRef,
  onTabChange,
  onClose,
  onUploadClick,
  onUploadSlotClick,
  onFileChange,
  onCardClick,
  onDeleteFile,
  maxFileSizeMb,
  hasUploadOption = true,
  hasDeleteOption = true,
  hideTabs = false,
  operationSections,
  initialExpandedOperationId,
  onUploadToOperation,
}) => {
  const { t } = useTranslation();
  const fileListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedFileId || !fileListRef.current) return;
    const selectedCard = fileListRef.current.querySelector(`[data-file-id="${selectedFileId}"]`);
    if (selectedCard) {
      selectedCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedFileId]);

  const renderMonitorSlot = (slot: 'before' | 'after', slotFiles: FileData[]) => (
    <Box
      key={slot}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          alignSelf: 'stretch',
        }}
      >
        <Typography
          sx={{
            color: '#504E5A',
            textAlign: 'center',
            fontFamily: '"Neue Frutiger World"',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '130%',
          }}
        >
          {slot === 'before' ? 'Before' : 'After'}
        </Typography>

        {hasUploadOption && (
          <UploadContainer sx={{ mt: 0 }}>
            <Tooltip
              arrow
              placement="top"
              title={maxFileSizeMb ? t('tooltips.max_file_size', { size: maxFileSizeMb }) : ''}
              disableHoverListener={!maxFileSizeMb}
            >
              <span>
                <AppButton
                  variant="primary"
                  size="small"
                  startIcon={<UploadIcon />}
                  data-testid={`monitor-upload-${slot}-button`}
                  onClick={() => onUploadSlotClick?.(slot)}
                >
                  {t('buttons.upload_image')}
                </AppButton>
              </span>
            </Tooltip>
          </UploadContainer>
        )}
      </Box>

      {slotFiles.length > 0 ? (
        slotFiles.map((file) => (
          <FileCard
            key={`${slot}-${file.objectId ?? file.id}`}
            file={file}
            active={selectedFileId === file.id}
            expanded={expandedFileIds.has(file.id)}
            onSelect={onCardClick}
            onDelete={(file.canDelete ?? hasDeleteOption) ? onDeleteFile : undefined}
            hasDeleteOption={file.canDelete ?? hasDeleteOption}
          />
        ))
      ) : (
        <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem', mb: 1 }}>
          No image uploaded.
        </Typography>
      )}
    </Box>
  );

  return (
    <RightSideContainer>
      <SidebarHeader>
        {!hideTabs && (
          <TabsContainer>
            {tabs.map((tab) => (
              <TabButton key={tab} active={activeTab === tab} onClick={() => onTabChange(tab)}>
                {tab}
              </TabButton>
            ))}
          </TabsContainer>
        )}

        <CloseButtonContainer sx={{ marginLeft: 'auto' }}>
          <CloseIconButton onClick={onClose} sx={{ color: 'black' }}>
            <CloseIcon />
          </CloseIconButton>
        </CloseButtonContainer>
      </SidebarHeader>

      {errorMessage && (
        <ErrorBanner>
          <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: '1.2rem' }} />
          <Typography sx={{ color: '#B91C1C', fontSize: '0.75rem' }}>{errorMessage}</Typography>
        </ErrorBanner>
      )}

      {!monitorMode && !operationSections && hasUploadOption && (
        <UploadContainer sx={{ mt: errorMessage ? 1 : 0 }}>
          <Tooltip
            arrow
            placement="top"
            title={maxFileSizeMb ? t('tooltips.max_file_size', { size: maxFileSizeMb }) : ''}
            disableHoverListener={!maxFileSizeMb}
          >
            <span>
              <AppButton
                variant="primary"
                size="small"
                startIcon={<UploadIcon />}
                onClick={onUploadClick}
              >
                {t('buttons.upload_image')}
              </AppButton>
            </span>
          </Tooltip>
        </UploadContainer>
      )}

      <input
        type="file"
        multiple={!monitorMode}
        accept="image/*,application/pdf,image/tiff,video/mp4"
        ref={fileInputRef as any}
        data-testid="images-modal-file-input"
        style={{ display: 'none' }}
        onChange={onFileChange}
      />

      <FileListContainer ref={fileListRef}>
        {monitorMode ? (
          <>
            {renderMonitorSlot('before', beforeFiles)}
            {renderMonitorSlot('after', afterFiles)}
          </>
        ) : operationSections ? (
          <OperationSectionList
            sections={operationSections}
            initialExpandedOperationId={initialExpandedOperationId}
            hasUploadOption={hasUploadOption}
            maxFileSizeMb={maxFileSizeMb}
            onUploadToOperation={onUploadToOperation ?? noop}
            renderFile={(file) => (
              <FileCard
                file={file}
                active={selectedFileId === file.id}
                expanded={expandedFileIds.has(file.id)}
                onSelect={onCardClick}
                onDelete={(file.canDelete ?? hasDeleteOption) ? onDeleteFile : undefined}
                hasDeleteOption={file.canDelete ?? hasDeleteOption}
              />
            )}
          />
        ) : files.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: '#9CA3AF', mt: 4, fontSize: '0.875rem' }}>
            No images in {activeTab}.
          </Typography>
        ) : (
          files.map((file) => (
            <div key={file.id} data-file-id={file.id}>
              <FileCard
                file={file}
                active={selectedFileId === file.id}
                expanded={expandedFileIds.has(file.id)}
                onSelect={onCardClick}
                onDelete={(file.canDelete ?? hasDeleteOption) ? onDeleteFile : undefined}
                hasDeleteOption={file.canDelete ?? hasDeleteOption}
              />
            </div>
          ))
        )}
      </FileListContainer>
    </RightSideContainer>
  );
};

export default ImageSidebar;
